import { organizations, adminUnits, contactPersons } from './itemsFixtureData';

/**
 * DL-65674 (epic — dl-58221 part 3, "cursor pagination integrity, nulls & ties"). Real, persisted
 * Items rows sized to span at least 3 pages (`perPage = 100` in `items.ts`) so the cursor's page
 * continuity, null-grouping and tie-break-by-id behaviour are genuinely exercised rather than
 * observed on a single page. Three isolated groups (own `?search=` token each, so one spec's
 * 210-row group never leaks into another's):
 *
 * - **BC** (`zzCursorProbeBC`, 210 rows) — Brand Contact. 30 rows null (753698, nulls-ASC), 180
 *   rows cycling the 4 real `contactPersons` (45 each) — a large enough tie per name for 753712's
 *   stable id-ascending sub-order check. Serves 753695 (continuity), 753706 (no-duplicate-ids),
 *   753698 (nulls ASC), 753712 (tie-break).
 * - **SC** (`zzCursorProbeSC`, 210 rows) — Supplier Contact. 90 rows null, 120 cycling the same 4
 *   contacts (30 each) — sized so the non-null block (indices 0-119 ascending) straddles the
 *   page-1/page-2 boundary at index 100, the same way the BC and CF groups do; a 60/150 split put
 *   the whole non-null cluster inside one page, making the boundary comparison a null-vs-null
 *   no-op. Serves 753696 (continuity), 753707 (no-skip), 753699 (nulls DESC).
 * - **CF** (`zzCursorProbeCF`, 210 rows) — customFieldId 9 ("Internal notes", Text). 160 rows with
 *   a distinct per-row value (`CF Value <n>`, zero-padded so lexicographic order matches insertion
 *   order — this group does not need numeric-vs-lexicographic rigor, only per-row distinctness),
 *   50 rows with no CF9 entry. Serves 753697 (continuity), 753700 (no-value grouping ASC).
 *
 * Every row is a REAL row in `itemsData` (not an `apiTransform` GET override, which would bypass
 * `sortItems`/`applyFilters` and only prove a hardcoded order — see the epic's own fixture note).
 * Rows are pushed in id-ascending order within each group so the mock's sort-STABILITY (`[...data]
 * .sort(...)`, V8 stable) keeps ties in insertion order, which is what 753712 asserts.
 */

let probeId = 951000;

const cfEntry = (value: string) => ({
  id: probeId + 500000,
  customFieldId: 9,
  name: 'Internal notes',
  userDefinedId: 'NOTES',
  type: 2,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: false,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
});

// Narrowly typed (not `Record<string, unknown>[]`) so this fixture's `customFields` entries keep
// the concrete shape `cfEntry` produces — a widened type here leaks into `itemsData`'s inferred
// element union and poisons unrelated, pre-existing code elsewhere in items.ts
// (`mergeCustomFields`'s `cf.customFieldId`) that has nothing to do with this fixture. Every
// fixture file contributing to `itemsData` must keep its `customFields` shape this concrete for
// the same reason (same fix applied on DL-65655's fixture files).
type RowOverrides = {
  companyContactPerson?: { id: string; name: string } | null;
  supplierContactPerson?: { id: string; name: string } | null;
  customFields?: ReturnType<typeof cfEntry>[];
};

const cursorProbeRow = (name: string, userDefinedId: string, overrides: RowOverrides) => ({
  id: probeId++,
  name,
  userDefinedId,
  description: null,
  brand: { id: 9101, name: 'CursorProbe Brand' },
  organization: organizations[0],
  supplier: { id: 9102, name: 'CursorProbe Supplier' },
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-03-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 7,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [] as ReturnType<typeof cfEntry>[],
  ...overrides,
});

// --- Brand Contact group (210 rows: 30 null, 180 cycling 4 contacts = 45 each) ---
const bcRows = [
  ...Array.from({ length: 30 }, (_, i) =>
    cursorProbeRow(`zzCursorProbeBC Null ${String(i).padStart(3, '0')}`, `CUR-BC-N${i}`, {
      companyContactPerson: null,
    })
  ),
  ...Array.from({ length: 180 }, (_, i) =>
    cursorProbeRow(`zzCursorProbeBC ${String(i).padStart(3, '0')}`, `CUR-BC-${i}`, {
      companyContactPerson: contactPersons[i % contactPersons.length],
    })
  ),
];

// --- Supplier Contact group (210 rows: 90 null, 120 cycling 4 contacts = 30 each) ---
const scRows = [
  ...Array.from({ length: 90 }, (_, i) =>
    cursorProbeRow(`zzCursorProbeSC Null ${String(i).padStart(3, '0')}`, `CUR-SC-N${i}`, {
      supplierContactPerson: null,
    })
  ),
  ...Array.from({ length: 120 }, (_, i) =>
    cursorProbeRow(`zzCursorProbeSC ${String(i).padStart(3, '0')}`, `CUR-SC-${i}`, {
      supplierContactPerson: contactPersons[i % contactPersons.length],
    })
  ),
];

// --- Custom field group (210 rows: 160 distinct CF9 values, 50 no CF9 entry) ---
const cfRows = [
  ...Array.from({ length: 160 }, (_, i) =>
    cursorProbeRow(`zzCursorProbeCF ${String(i).padStart(3, '0')}`, `CUR-CF-${i}`, {
      customFields: [cfEntry(`CF Value ${String(i).padStart(3, '0')}`)],
    })
  ),
  ...Array.from({ length: 50 }, (_, i) =>
    cursorProbeRow(`zzCursorProbeCF Null ${String(i).padStart(3, '0')}`, `CUR-CF-N${i}`, {
      customFields: [],
    })
  ),
];

export const itemsSortingCursorProbeRows = [...bcRows, ...scRows, ...cfRows];
